import { useEffect, useRef, useCallback, useState } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function TurnstileWidget({ onVerify, onExpire, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current) return;
    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch {}
      widgetIdRef.current = null;
    }
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => { setStatus('ready'); onVerify?.(token); },
        'expired-callback': () => { setStatus('loading'); onExpire?.(); },
        'error-callback': () => {
          setStatus('error');
          // Auto-bypass on widget error (domain not configured)
          onVerify?.('turnstile_widget_error');
        },
        theme: 'light',
        size: 'normal',
      });
    } catch (e) {
      console.error('Turnstile render error:', e);
      setStatus('error');
      onVerify?.('turnstile_widget_error');
    }
  }, [onVerify, onExpire]);

  useEffect(() => {
    if (!SITE_KEY) { onVerify?.('no_site_key'); return; }
    const checkInterval = setInterval(() => {
      if (window.turnstile) { clearInterval(checkInterval); renderWidget(); }
    }, 200);
    // Fallback: if Turnstile doesn't load in 8 seconds, auto-bypass
    const fallbackTimeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (status === 'loading') {
        setStatus('error');
        onVerify?.('turnstile_load_timeout');
      }
    }, 8000);
    return () => {
      clearInterval(checkInterval);
      clearTimeout(fallbackTimeout);
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget, resetKey, status, onVerify]);

  if (!SITE_KEY) return null;
  return (
    <div className="mb-3">
      <div ref={containerRef} data-testid="turnstile-widget" className="flex justify-center" />
      {status === 'error' && (
        <p className="text-xs text-amber-600 text-center mt-1" data-testid="turnstile-fallback">
          <i className="fa-solid fa-shield-halved mr-1" />Protected by rate limiting &amp; honeypot
        </p>
      )}
    </div>
  );
}
